import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Search as SearchIcon, Network, Compass, Languages, Globe, Hammer, Bell, FileText, Code2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { bff, toClientError } from '../services/bff'
import SearchPage from './SearchPage'
import KnowledgeMap from '../components/KnowledgeMap'
import TopicsPanel from '../components/TopicsPanel'
import TranslatePanel from '../components/TranslatePanel'
import DiscoverPanel from '../components/DiscoverPanel'
import ImpactPanel from '../components/ImpactPanel'
import WatchesPanel from '../components/WatchesPanel'
import ReportsPanel from '../components/ReportsPanel'
import CodeExplorer from '../components/CodeExplorer'

const MODES = [
  ['ask', 'Ask & Search', SearchIcon],
  ['map', 'Knowledge Map', Network],
  ['topics', 'Topics', Compass],
  ['discover', 'Discover', Globe],
  ['impact', 'Impact', Hammer],
  ['code', 'Code', Code2],
  ['watches', 'Watches', Bell],
  ['reports', 'Reports', FileText],
  ['translate', 'Translate', Languages],
]

export default function ResearchPage() {
  const [mode, setMode] = useState('ask')
  const [seed, setSeed] = useState({ q: '', nonce: 0 })
  const [discoverSeed, setDiscoverSeed] = useState({ q: '', nonce: 0 })
  // /research?discover=<query> (Overviews' coverage check and gap audit link
  // here) opens Discover seeded with that query.
  // A visited tab stays mounted, so this has to follow the navigation: on mount
  // alone only the first link would ever search. The location key also tells two
  // clicks on the same link apart.
  const [params] = useSearchParams()
  const { key: navigation } = useLocation()
  const handled = useRef(null)
  useEffect(() => {
    const d = params.get('discover')
    if (!d || handled.current === navigation) return
    handled.current = navigation
    setDiscoverSeed({ q: d, nonce: Date.now() })
    setMode('discover')
  }, [navigation]) // eslint-disable-line react-hooks/exhaustive-deps

  // Unread watch changes → a count badge on the Watches tab, so a rule that
  // fired while you were elsewhere is visible without opening the mode. Shares
  // the ['watches'] cache/poll with WatchesPanel.
  const watchesQ = useQuery({
    queryKey: ['watches'],
    queryFn: () => bff.get('/api/watches').then((r) => r.data).catch((e) => Promise.reject(toClientError(e))),
    refetchInterval: 60000,
  })
  const unread = (watchesQ.data?.watches ?? []).filter((w) => w.unread).length

  // Clicking a concept/topic in Map or Topics seeds an Ask question.
  const pick = (label) => {
    setSeed({ q: `What does the archive say about "${label}"?`, nonce: Date.now() })
    setMode('ask')
  }

  // "Find sources" on a topic/gap → seeds Discover (gap-driven acquisition).
  const discover = (label) => {
    setDiscoverSeed({ q: label, nonce: Date.now() })
    setMode('discover')
  }

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold-deep dark:text-gold mb-2">Illuminating Truth</div>
        <h1 className="font-display text-4xl text-ink dark:text-mist">Research</h1>
        <p className="mt-2 text-ink-soft dark:text-mist/60">
          Ask your archive, map its concepts, and find where to dig next.
        </p>
      </header>

      <div className="inline-flex rounded-lg border border-parchment-border dark:border-midnight-600 p-0.5">
        {MODES.map(([m, label, Icon]) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition ${
              mode === m ? 'bg-gold/15 text-gold-deep dark:text-gold' : 'text-ink-soft dark:text-mist/60'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {m === 'watches' && unread > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-gold text-[10px] font-semibold text-midnight-900 leading-none">
                {unread}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Keep every mode mounted so its state/progress survives mode switches. */}
      <div style={{ display: mode === 'ask' ? 'block' : 'none' }}>
        <SearchPage embedded seedQuestion={seed.q} seedNonce={seed.nonce} />
      </div>
      <div style={{ display: mode === 'map' ? 'block' : 'none' }}>
        <KnowledgeMap onPickConcept={pick} />
      </div>
      <div style={{ display: mode === 'topics' ? 'block' : 'none' }}>
        <TopicsPanel onPickConcept={pick} onDiscover={discover} />
      </div>
      <div style={{ display: mode === 'discover' ? 'block' : 'none' }}>
        <DiscoverPanel seedQuery={discoverSeed.q} seedNonce={discoverSeed.nonce} />
      </div>
      <div style={{ display: mode === 'impact' ? 'block' : 'none' }}>
        <ImpactPanel />
      </div>
      <div style={{ display: mode === 'code' ? 'block' : 'none' }}>
        <CodeExplorer />
      </div>
      <div style={{ display: mode === 'watches' ? 'block' : 'none' }}>
        <WatchesPanel />
      </div>
      <div style={{ display: mode === 'reports' ? 'block' : 'none' }}>
        <ReportsPanel />
      </div>
      <div style={{ display: mode === 'translate' ? 'block' : 'none' }}>
        <TranslatePanel />
      </div>
    </div>
  )
}
